import { IPrinterInfo } from "@/interfaces/printer/printer";
import { calculateCpuUsage, formatMemoryUsage } from "./common";
import PrinterConfigModule from "@/store/modules/printers-config";
import NotificationService from "@/services/notification.service";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { LogService } from "@/services/logging";
import { EventType } from "@smeetz/compliance-common";
import { processAdditionalEvents } from "./compliance";

export function printCommonReceipt(commonReceipt: string) {
  const electronApi = window?.electronAPI;

  if (electronApi) {
    // electronApi.printReceipt(receiptInfo);
    electronApi.printCommonReceipt(commonReceipt);
  }
}

/**
 * Opens the cash drawer using the best available method:
 * - Electron exposing openCashDrawer: uses the dedicated API
 * - Older Electron / French fork / browser: falls back to printing an empty receipt
 */
export function openCashDrawer() {
  const electronApi = window?.electronAPI;
  if (!electronApi) {
    return;
  }

  if (typeof electronApi.openCashDrawer === "function") {
    const printerName = PrinterConfigModule.receiptPrinter;
    if (printerName) {
      electronApi.openCashDrawer(printerName);
    } else {
      NotificationService.showCustomMessage(
        t("error.no-selected-receipt-printer"),
        "error"
      );
    }
  } else {
    // Fallback when the native API is not exposed (older builds, French fork): print an empty receipt to trigger the drawer
    electronApi.printCommonReceipt("");
  }
  processAdditionalEvents(EventType.DRAWER_OPEN).catch((error) => {
    LogService.info("Error processing DRAWER OPEN event", {
      error,
    });
  });
}

export function printCommonTicket(ticket: string) {
  const electronApi = window?.electronAPI;

  if (electronApi) {
    electronApi.printCommonTicket(ticket);
  }
}

export async function getAvailablePrinters(): Promise<IPrinterInfo[]> {
  const electronApi = window?.electronAPI;
  let printers: IPrinterInfo[] = [];
  if (electronApi) {
    try {
      printers = await electronApi.getAvailablePrinters();
    } catch (error) {
      console.log(error);
    }
  }
  return printers;
}

export function printWithPrinter(
  printerName: string,
  document: string,
  type: number
) {
  const electronApi = window?.electronAPI;

  if (electronApi) {
    electronApi.printWithPrinter(printerName, document, type);
  }
}

export async function performanceInfo() {
  const electronApi = window?.electronAPI;
  if (electronApi) {
    try {
      const data = await electronApi.performanceInfo();
      const {
        totalCpuUsage,
        processCpuUsage,
        memoryUsage,
        totalMemory,
        freeMemory,
      } = data;
      const parsedMemoryUsage = JSON.parse(memoryUsage);
      const parsedtotalCpuUsage = JSON.parse(totalCpuUsage);
      const totalCpuUsagePercentage = calculateCpuUsage(parsedtotalCpuUsage);
      const fixedProcessCpuUsage = Number(processCpuUsage).toFixed(2);
      return {
        totalMemory: formatMemoryUsage(Number(totalMemory)),
        freeMemory: formatMemoryUsage(Number(freeMemory)),
        totalCpuUsage: `${totalCpuUsagePercentage}%`, // CPU usage by the whole system
        processCpuUsage: `${fixedProcessCpuUsage}%`, // Percentage of CPU used since the last call. First call returns 0.
        memoryUsage: {
          rss: `${formatMemoryUsage(parsedMemoryUsage.rss)}`, // Resident Set Size - total memory allocated for the process execution
          heapTotal: `${formatMemoryUsage(parsedMemoryUsage.heapTotal)}`, // total size of the allocated heap
          heapUsed: `${formatMemoryUsage(parsedMemoryUsage.heapUsed)}`, // actual memory used during the execution
          external: `${formatMemoryUsage(parsedMemoryUsage.external)}`, // V8 external memory
        },
      };
    } catch (error) {
      console.log(error);
    }
  }
}
